// components/artifacts/panel/PanelResizeHandle.tsx
import React, { useCallback, useRef } from "react";
import { cn } from "@/lib/utils";

interface PanelResizeHandleProps {
  onResize: (widthPercentage: number) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
  minWidthPercent?: number;
  maxWidthPercent?: number;
}

export const PanelResizeHandle: React.FC<PanelResizeHandleProps> = ({
  onResize,
  onResizeStart,
  onResizeEnd,
  minWidthPercent = 20,
  maxWidthPercent = 80,
}) => {
  const animationFrameRef = useRef<number>();
  const isResizingRef = useRef(false);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingRef.current = true;
    onResizeStart?.();
    
    const handleMouseMove = (e: MouseEvent) => {
      // Cancel previous animation frame to throttle updates
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      animationFrameRef.current = requestAnimationFrame(() => {
        const windowWidth = window.innerWidth;
        const mouseX = e.clientX;
        const newWidth = ((windowWidth - mouseX) / windowWidth) * 100;
        
        // Constrain between min and max
        const constrainedWidth = Math.max(minWidthPercent, Math.min(maxWidthPercent, newWidth));
        onResize(constrainedWidth);
      });
    };
    
    const handleMouseUp = () => {
      isResizingRef.current = false;
      onResizeEnd?.();
      
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Reset cursor styles
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.body.style.pointerEvents = '';
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    // Set cursor styles
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.body.style.pointerEvents = 'none';
  }, [onResize, onResizeStart, onResizeEnd, minWidthPercent, maxWidthPercent]);

  return (
    <div
      className={cn(
        "absolute left-0 top-0 h-full w-1 cursor-col-resize",
        "bg-transparent hover:bg-blue-500/20 transition-colors",
        "active:bg-blue-500/40"
      )}
      onMouseDown={handleMouseDown}
    >
      <div className="absolute left-1/2 top-1/2 h-8 w-3 -translate-x-1/2 -translate-y-1/2 rounded-sm bg-gray-300/50 opacity-0 hover:opacity-100 transition-opacity">
        <div className="flex h-full w-full items-center justify-center">
          <div className="h-4 w-0.5 bg-gray-400 rounded-full mx-0.5" />
          <div className="h-4 w-0.5 bg-gray-400 rounded-full mx-0.5" />
        </div>
      </div>
    </div>
  );
};